'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  href: string;
  cta: string;
  badge?: string;
  delay?: number;
}

function FeatureCard({ icon, title, description, href, cta, badge, delay = 0 }: FeatureCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl border border-blue-100 shadow-sm hover:shadow-md hover:border-blue-300 transition-all duration-300 flex flex-col overflow-hidden group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms, box-shadow 0.2s ease, border-color 0.2s ease`,
      }}
    >
      <div className="p-6 flex-1">
        <div className="flex items-start justify-between mb-4">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-2xl border border-blue-100">
            {icon}
          </div>
          {badge && (
            <span className="text-xs px-2 py-1 rounded-full bg-blue-600 text-white font-semibold">{badge}</span>
          )}
        </div>
        <h3 className="text-base font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
      </div>
      <div className="px-6 pb-6">
        <Link
          href={href}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 group-hover:gap-2.5 transition-all"
        >
          {cta}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

const FEATURES: FeatureCardProps[] = [
  {
    icon: '📄',
    title: 'AI Resume Builder',
    description: 'Enter your real information, choose from ATS-friendly templates, get AI suggestions on your existing content, and export a clean PDF.',
    href: '/resume-builder',
    cta: 'Build My Resume',
    delay: 0,
  },
  {
    icon: '🔍',
    title: 'ATS Resume Analyzer',
    description: 'Analyze your resume against ATS systems, get keyword recommendations, and improve with AI-powered suggestions — without fabricating experience.',
    href: '/ats-analyzer',
    cta: 'Analyze Resume',
    badge: 'Premium',
    delay: 80,
  },
  {
    icon: '🎓',
    title: 'Scholarship Finder',
    description: 'Get matched against real, verified scholarships. Every result includes the official source URL, deadline, and eligibility requirements.',
    href: '/scholarships',
    cta: 'Find Scholarships',
    delay: 160,
  },
  {
    icon: '🔎',
    title: 'Job Description Analyzer',
    description: 'Paste any job description to extract required skills, ATS keywords, and get role-specific resume tips to improve your application.',
    href: '/job-analyzer',
    cta: 'Analyze a Job',
    badge: 'Premium',
    delay: 240,
  },
  {
    icon: '🤖',
    title: 'AI Career Assistant',
    description: 'Natural-language career search powered by CareerLift AI. Ask about jobs, scholarships, interview prep, and career paths.',
    href: '/career-search',
    cta: 'Search Careers',
    delay: 320,
  },
  {
    icon: '🎬',
    title: 'Video Templates',
    description: 'Vertical 9:16 templates for TikTok, Instagram Reels, and YouTube Shorts. Customize hooks, scripts, captions, and branding.',
    href: '/video-templates',
    cta: 'Browse Templates',
    delay: 400,
  },
];

export default function FeaturesSection() {
  return (
    <section className="py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-blue-50/50" id="features">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 bg-blue-50 text-blue-600 border border-blue-200">
            Six Tools, One Platform
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
            Everything you need to{' '}
            <span className="text-blue-600">move your career forward</span>
          </h2>
          <p className="mt-3 text-slate-500 max-w-lg mx-auto text-sm sm:text-base">
            From resume building to scholarship matching — all your career tools in one place.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {FEATURES.map(f => <FeatureCard key={f.href} {...f} />)}
        </div>
      </div>
    </section>
  );
}
